<script lang="ts">
	import { Indicator } from "flowbite-svelte";
	import { createEventDispatcher, getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import type { Suite } from "#gen/bundles/v1/bundle_pb.js";
	import type { ImageJson, Point } from "#gen/bundles/v1/common_pb.js";
	import type { EntryAsset } from "#lib/assetManager.js";
	import FocalImage from "#lib/components/FocalImage.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import FileManagerDialog from "#lib/components/images/file-management/FileManagerDialog.svelte";
	import type { ImagePickerChangeEvent } from "#lib/components/images/types.js";
	import { interpolate } from "#lib/stores/asset.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import Button from "#lib/ui/Button/index.js";
	import type { Writable } from "svelte/store";

	type $$Events = HTMLElementEventMap & {
		change: CustomEvent<ImageJson>;
		remove: CustomEvent;
	};

	const dispatch = createEventDispatcher();
	const suite: Writable<Suite> = getContext("suite");
	const entryAsset: Writable<EntryAsset | undefined> = getContext("entryAsset");
	const provider: Writable<TokenProvider> = getContext("tokenProvider");

	export let photo = "";
	export let focalPoint: Point | undefined = undefined;
	export let aspectRatio: number | undefined = undefined;
	export let initialAspectRatio: number | undefined = undefined;
	export let cropperCaption: string | undefined = undefined;
	export let allowFocalPointSelection = false;
	export let showAspectRatioPicker = false;

	let preview: string | undefined = photo;
	let editing = false;

	$: renderPreview($provider, entryAsset, photo);

	function valueChanged(e: CustomEvent<ImagePickerChangeEvent>) {
		photo = e.detail.value.trim();
		if (photo) {
			renderPreview($provider, entryAsset, photo);
		} else {
			preview = undefined;
		}
		// Convert ImagePickerChangeEvent to ImageJson
		const imageJson: ImageJson = {
			url: e.detail.value,
			focalPoint: e.detail.focalPoint,
		};
		dispatch("change", imageJson);
	}

	async function renderPreview(provider: TokenProvider, entryAsset: Writable<EntryAsset | undefined>, url: string) {
		if (provider?.hasSubstitutionToken(url)) {
			try {
				preview = interpolate($suite, entryAsset, url);
			} catch {
				preview = url;
			}
		} else {
			preview = url;
		}
	}
</script>

<div role="img" class={twMerge("group/image-tile relative", $$props.class)} draggable={true} on:dragstart on:drop|preventDefault on:dragover|preventDefault={() => false}>
	<Indicator size="xl" placement="top-right" class="z-10 hidden group-hover/image-tile:inline">
		<Button color="none" on:click={() => dispatch("remove")} data-action-name="remove-image" class="m-auto cursor-pointer p-1">
			<Icon iconSet="heroicons" name="x-mark" class="h-4 w-4" />
		</Button>
	</Indicator>
	<Button
		class="focus-within:border-primary-500 focus-within:outline-primary-500 focus-visible:border-primary-500 focus-visible:outline-primary-500 aspect-square w-full overflow-clip border p-0 focus-within:ring-0 focus-within:outline-1 focus-within:outline-offset-0 focus-visible:ring-0 focus-visible:outline-1 focus-visible:outline-offset-0"
		color="none"
		on:click={() => (editing = true)}
		data-action-name="image-tile"
	>
		<FocalImage layout="fixed" height={100} width={100} src={preview ?? photo} style="width: 100%; height: 100%;" {focalPoint} />
	</Button>
</div>
{#if editing}
	<FileManagerDialog bind:open={editing} value={photo} {focalPoint} on:change={valueChanged} {aspectRatio} {initialAspectRatio} caption={cropperCaption} {allowFocalPointSelection} {showAspectRatioPicker} />
{/if}
